import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { InstantSearch, Configure, Index } from 'react-instantsearch';
import { noEmptySearchClient } from 'components/essentials/AlgoliaSearchClient';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import useOpenSearch from 'global-state/isSearchOpen/useOpenSearch';
import useIsSearchOpen from 'global-state/isSearchOpen/useIsSearchOpen';
import useCloseSearch from 'global-state/isSearchOpen/useCloseSearch';
import useIsAdvancedSearch from './hooks/useIsAdvancedSearch';
import { CategoryHits, ResultsByCategory } from './commons/helpers';
import SearchResultsWrapper from './SearchResultsWrapper';
import ProductGrid from './ProductGrid';
import SeeFullResultsButton from './SeeFullResultsButton';
import JBSearchBox from './components/JBSearchBox';

/**
 * Main consumer search results component (Version for old Consumer app)
 *
 * Features:
 * - 3-column responsive layout (Products, Categories, View Results By)
 * - Product grid with hover effects and pricing
 * - Dynamic hit counts and search state management
 * - Accessibility improvements and performance optimizations
 */
const ConsumerSearchBarLegacy = () => {
  const $ = _c(27);
  const [isSearchBarActivated, setisSearchBarActivated] = useState(false);
  const closeSearch = useCloseSearch();
  const openSearch = useOpenSearch();
  const isSearchOpen = useIsSearchOpen();
  const isAdvancedSearch = useIsAdvancedSearch();
  let t0;
  if ($[0] !== closeSearch) {
    t0 = e => {
      if (e.key === "Enter" || e.key === " ") {
        closeSearch();
      }
    };
    $[0] = closeSearch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleOverlayKeyDown = t0;
  let t1;
  if ($[2] !== openSearch) {
    t1 = () => {
      setisSearchBarActivated(true);
      openSearch();
    };
    $[2] = openSearch;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSearchBarFocus = t1;
  let t2;
  if ($[4] !== isAdvancedSearch) {
    t2 = classNames("bg-white max-[1023px]:relative max-[1023px]:z-10", {
      hidden: isAdvancedSearch
    });
    $[4] = isAdvancedSearch;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Configure hitsPerPage={3} facets={["*"]} numericFilters={["item_availability<2", "visibility>0", "visibility<4"]} attributesToRetrieve={["_highlightResult", "product_id", "name"]} />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== closeSearch || $[8] !== handleSearchBarFocus) {
    t4 = <JBSearchBox autoFocus={true} isActive={true} setInactive={_temp} toggleSearchOpen={closeSearch} variant="ConsumerInstantSearch" handleSearchBarFocus={handleSearchBarFocus} />;
    $[7] = closeSearch;
    $[8] = handleSearchBarFocus;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== closeSearch || $[11] !== isSearchOpen) {
    t5 = isSearchOpen && <SearchResultsWrapper><div className="bg-white absolute w-full" data-xc="search-dropdown-preview"><div className="flex w-full max-w-none mx-auto px-2 gap-6"><div className="flex-1"><div className="bg-gray-50 p-6 rounded-lg" data-xc="search-dropdown-products"><ProductGrid /><div className="mt-6 flex justify-center"><SeeFullResultsButton /></div></div></div><div className="w-64"><div className="bg-gray-50 p-0 rounded-lg h-full" data-xc="search-dropdown-categories"><Index indexName="categories"><Configure hitsPerPage={5} numericFilters={["fk_store_id=1"]} facetFilters={["is_searchable:1"]} attributesToRetrieve={["objectID", "category_name", "category_sef_url", "url"]} /><CategoryHits toggleSearchOpen={closeSearch} /></Index></div></div><div className="w-64"><div className="bg-gray-50 p-6 rounded-lg h-full" data-xc="search-dropdown-results-by"><Index indexName="reviews"><Configure facets={["*"]} numericFilters="" facetFilters="" /></Index><Index indexName="customer_photos"><Configure facets={["*"]} numericFilters="" /></Index><Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}><Configure numericFilters={["is_active=1", "show_in_search=1"]} /></Index><ResultsByCategory toggleSearchOpen={closeSearch} /></div></div></div></div></SearchResultsWrapper>;
    $[10] = closeSearch;
    $[11] = isSearchOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="bg-white relative max-[1023px]:z-10 lg:!z-[1002]"><InstantSearch searchClient={noEmptySearchClient} indexName={ALGOLIA_INDEX_NAME.PRODUCTS}>{t3}{t4}{t5}</InstantSearch></div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = !isSearchOpen || !isSearchBarActivated;
  const t8 = isSearchOpen && isSearchBarActivated;
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = classNames("fixed inset-0 bg-blackOverlay z-[1001] transition-opacity duration-300 ease-out", {
      hidden: t7,
      block: t8
    });
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== closeSearch || $[20] !== handleOverlayKeyDown || $[21] !== t9) {
    t10 = <div className={t9} onClick={closeSearch} role="button" tabIndex={0} onKeyDown={handleOverlayKeyDown} aria-label="Close search modal" />;
    $[19] = closeSearch;
    $[20] = handleOverlayKeyDown;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t2 || $[25] !== t6) {
    t11 = <div className={t2}>{t6}{t10}</div>;
    $[23] = t10;
    $[24] = t2;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
export default ConsumerSearchBarLegacy;
function _temp() {}